/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import axios from 'axios';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import { API_URL } from '../../config/config';
import { InventoryType } from './inventory.types';

const setNestedValue = (obj: any, path: string, value: any): any => {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  const newObj = { ...obj };
  let cur = newObj;
  for (const key of keys) {
    if (cur[key] === undefined) cur[key] = {};
    cur = cur[key];
  }
  cur[lastKey] = value;
  return newObj;
};

const UpdateInventory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<InventoryType>({
    name: '',
    quantity: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const normalizeInventory = (data: any): InventoryType => ({
    name: data.name ?? '',
    quantity: data.quantity ?? null,
  });

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        if (!user) return;
        setLoading(true);
        const res = await axios.get(`${API_URL}/inventory/${user.id}/${id}`);
        const rawData = res.data.data; // API returns the inventory inside data.data
        const normalizedData = normalizeInventory(rawData);
        setFormData(normalizedData);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to fetch inventory');
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading && user) fetchInventory();
  }, [user, isLoading, id]);

  const handleSubmit = async () => {
    if (!formData || !user) return;

    try {
      setSaving(true);

      const payload: any = {
        ...formData,
        // convert numeric fields properly
        quantity: formData.quantity ?? null,
        expiry_date: formData.expiry_date ?? null,
      };

      await axios.put(`${API_URL}/inventory/${user.id}/${id}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      navigate(`/inventory/${id}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (!prev) return prev;
      if (name.includes('.')) {
        return setNestedValue(prev, name, value);
      } else {
        return { ...prev, [name]: value };
      }
    });
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (!prev) return prev;
      return { ...prev, [name]: value };
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => {
      if (!prev) return prev;
      return { ...prev, [name]: checked };
    });
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
      }}
    >
      <div style={{ height: 10 }}></div>
      <TextField
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
      />

      <TextField
        label="Quantity"
        name="quantity"
        type="number"
        value={formData.quantity}
        onChange={handleChange}
        required
      />

      <TextField
        label="Expiry Date"
        name="expiry_date"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={formData.expiry_date ?? ''}
        onChange={handleChange}
      />

      <TextField
        label="Batch Number"
        name="batch_number"
        value={formData.batch_number ?? ''}
        onChange={handleChange}
      />

      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select
          name="status"
          value={formData.status ?? ''}
          label="Status"
          onChange={handleSelectChange}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="discontinued">Discontinued</MenuItem>
          <MenuItem value="out_of_stock">Out of Stock</MenuItem>
        </Select>
      </FormControl>

      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Update Store'}
        </Button>
      </Box>
      <div style={{ height: 20 }}></div>
    </Box>
  );
};

export default UpdateInventory;
